import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetShowTechNotes, apiCreateShowTechNote, apiUpdateShowTechNote, apiDeleteShowTechNote,
  apiGetShowMiscNotes, apiCreateShowMiscNote, apiUpdateShowMiscNote, apiDeleteShowMiscNote,
  apiGetShowCategorizedNotes, apiCreateShowCategorizedNote, apiUpdateShowCategorizedNote, apiDeleteShowCategorizedNote,
} from '../api';
import { useWriteGate } from '../lib/writeGate';
import AutoTextarea from './AutoTextarea';

// Per-show notes — three sibling resources, each a separate daysheet section:
//   tech    (tech_notes   : content)              → TECH tab
//   misc    (misc_notes   : category + content)   → LOGISTICS tab
//   notes   (notes        : category + title + content) → LOGISTICS tab (3rd).
// The user picks which sections print in the Daysheet Layout editor. Writes go
// through the capability-aware write gate (server: makeResource, editor+ role).

interface CommonNote {
  id: number | string;
  tour_date_id?: number | string;
  category?: string;
  title?: string;
  content: string;
}

type Kind = 'tech' | 'misc' | 'notes';

interface Props {
  realId: string | null;
  /** The show's band — feeds the capability-aware write gate. */
  bandId?: string | null;
  kind: Kind;
  /**
   * Reports whether this section holds at least one note (LOT E1): the hosting
   * card opens itself when it does. No extra fetch — the section already loads
   * its own list. Fires on mount (`false`) then on every change.
   */
  onHasDataChange?: (hasData: boolean) => void;
  /**
   * LOT D3: when the hosting card ALREADY carries this section's title, the
   * inner heading duplicates it (one card, one title). Default false = unchanged.
   */
  hideHeading?: boolean;
}

/**
 * Keystroke → server debounce.
 *
 * Measured in production: every single character typed in a note field fired a
 * `PUT /api/shows/:id/{tech,misc,notes}/:nid`. A 50-char note = 50 PUTs in three
 * seconds, each one writing an `activity_log` row and broadcasting a WebSocket
 * message — and because `items` was only updated from the network RESPONSE, an
 * out-of-order reply re-wrote the field mid-typing (dropped letters, cursor
 * jumps). The section is optimistic: local state is updated synchronously on
 * every keystroke, and only the LAST value is persisted, 800 ms after the last
 * keystroke (or immediately on blur / unmount / delete).
 *
 * 800 ms and not 400: at 400 ms a normal typing rhythm (a micro-pause between
 * two words) flushed mid-sentence, so the debounce never did its job on the
 * fields people actually fill in (logistics notes, welcome note).
 */
const WRITE_DEBOUNCE_MS = 800;

const API = {
  tech: {
    get: apiGetShowTechNotes, create: apiCreateShowTechNote,
    update: apiUpdateShowTechNote, remove: apiDeleteShowTechNote,
    showCategory: false, showTitle: false,
  },
  misc: {
    get: apiGetShowMiscNotes, create: apiCreateShowMiscNote,
    update: apiUpdateShowMiscNote, remove: apiDeleteShowMiscNote,
    showCategory: true, showTitle: false,
  },
  notes: {
    get: apiGetShowCategorizedNotes, create: apiCreateShowCategorizedNote,
    update: apiUpdateShowCategorizedNote, remove: apiDeleteShowCategorizedNote,
    showCategory: true, showTitle: true,
  },
} as const;

export default function ShowNotesSection({ realId, bandId, kind, onHasDataChange, hideHeading = false }: Props) {
  const { t } = useTranslation();
  const writeGate = useWriteGate(bandId);
  const canWrite = writeGate.canWrite;

  const api = API[kind];
  const i18nBase = `showNotes.${kind}`;

  const [items, setItems] = useState<CommonNote[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Last locally-typed value per note (key = String(note.id)) waiting to be
  // persisted, and the debounce timer that will persist it.
  const pendingRef = useRef<Map<string, CommonNote>>(new Map());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Report "this section has content" upward (LOT E1) without re-subscribing
  // the effect to a callback identity: the ref always holds the latest one.
  const onHasDataRef = useRef(onHasDataChange);
  onHasDataRef.current = onHasDataChange;
  const hasData = items.length > 0;
  useEffect(() => { onHasDataRef.current?.(hasData); }, [hasData]);

  const load = useCallback(async () => {
    if (!realId) return;
    const list = (await api.get(realId).catch(() => [])) as CommonNote[];
    // A reload must never wipe keystrokes typed while the fetch was in flight:
    // pending local values win over the (older) server copy.
    setItems(list.map((item) => pendingRef.current.get(String(item.id)) ?? item));
    setLoaded(true);
  }, [realId, api]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    if (!canWrite) return;
    if (!realId) return;
    const item = await api.create(realId, { content: '' });
    setItems((p) => [...p, item as CommonNote]);
  };

  /** Persist (once) the last local value of one note; no-op when nothing is pending. */
  const sendPending = useCallback(async (key: string) => {
    const pending = pendingRef.current.get(key);
    if (!pending) return;
    // Cannot send right now (gate still loading, no show): keep the edit QUEUED
    // rather than dropping it — the next blur / keystroke / unmount retries.
    if (!canWrite || !realId) return;
    pendingRef.current.delete(key);
    const timer = timersRef.current.get(key);
    if (timer !== undefined) clearTimeout(timer);
    timersRef.current.delete(key);
    try {
      // The LOCAL value is the truth: `items` already holds exactly what the
      // user typed. Copying the server copy back into state re-renders the field
      // from an object built one keystroke ago (dropped letters, caret jump) —
      // the very "freeze every 400 ms" the user reported.
      await api.update(realId, pending.id, pending);
    } catch {
      // Offline / throttled: the optimistic value stays visible and the next
      // keystroke (or blur) retries with the whole note.
    }
  }, [api, realId, canWrite]);

  /** Flush one note now — used on blur so leaving the field never loses a write. */
  const flushNote = useCallback((id: string | number) => {
    void sendPending(String(id));
  }, [sendPending]);

  /**
   * Flush on unmount (tab switch, page change): the cleanup below runs with the
   * ids that were still pending and persists them against the show they belong to.
   */
  useEffect(() => {
    return () => {
      for (const key of Array.from(pendingRef.current.keys())) void sendPending(key);
    };
  }, [sendPending]);

  const update = (n: CommonNote, patch: Partial<CommonNote>) => {
    if (!canWrite) return;
    if (!realId) return;
    const key = String(n.id);
    const next = { ...n, ...patch };
    // Optimistic: the field is up to date BEFORE any network round-trip, so
    // fast typing needs no reply and no stale reply can jump the cursor.
    setItems((p) => p.map((x) => (String(x.id) === key ? next : x)));
    pendingRef.current.set(key, next);
    const previous = timersRef.current.get(key);
    if (previous !== undefined) clearTimeout(previous);
    timersRef.current.set(key, setTimeout(() => { void sendPending(key); }, WRITE_DEBOUNCE_MS));
  };

  const remove = async (n: CommonNote) => {
    if (!canWrite) return;
    if (!realId) return;
    const key = String(n.id);
    // Drop any queued edit first: a PUT landing after the DELETE would recreate
    // work for a note the user just deleted.
    const timer = timersRef.current.get(key);
    if (timer !== undefined) clearTimeout(timer);
    timersRef.current.delete(key);
    pendingRef.current.delete(key);
    await api.remove(realId, n.id);
    setItems((p) => p.filter((x) => String(x.id) !== key));
  };

  return (
    <div data-notes-section={kind}>
      {!hideHeading && (
        <h4 className="rc-section-title" style={{ margin: '0 0 4px' }}>{t(`${i18nBase}.title`)}</h4>
      )}
      <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 10px' }}>
        {t(`${i18nBase}.hint`)}
      </p>
      {loaded && items.length === 0 ? (
        <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 8px' }}>{t(`${i18nBase}.noNotes`)}</p>
      ) : null}
      <div className="rc-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((n) => (
          <div key={String(n.id)} className="rc-card col">
            <div className="rc-card-fields">
              {api.showTitle && (
                <input
                  className="form-input"
                  style={{ flex: '1 1 160px', minWidth: 0 }}
                  value={n.title || ''}
                  placeholder={t(`${i18nBase}.titlePh`)}
                  disabled={!canWrite}
                  onChange={(e) => update(n, { title: e.target.value })}
                  onBlur={() => flushNote(n.id)}
                />
              )}
              {api.showCategory && (
                <input
                  className="form-input"
                  style={{ flex: '1 1 140px', minWidth: 0 }}
                  value={n.category || ''}
                  placeholder={t(`${i18nBase}.categoryPh`)}
                  disabled={!canWrite}
                  onChange={(e) => update(n, { category: e.target.value })}
                  onBlur={() => flushNote(n.id)}
                />
              )}
              <button className="btn btn-small btn-danger" style={{ flex: '0 0 auto', marginLeft: 'auto' }} onClick={() => remove(n)} disabled={!canWrite}>✕</button>
            </div>
            <div className="rc-card-fields" style={{ marginTop: 6 }}>
              <AutoTextarea
                className="form-input"
                rows={2}
                style={{ flex: '1 1 100%', minWidth: 0 }}
                value={n.content || ''}
                placeholder={t(`${i18nBase}.contentPh`)}
                disabled={!canWrite}
                onChange={(e) => update(n, { content: e.target.value })}
                onBlur={() => flushNote(n.id)}
              />
            </div>
          </div>
        ))}
      </div>
      <button className="btn btn-primary btn-small" onClick={add} disabled={!canWrite} style={{ marginTop: 8 }}>
        {t(`${i18nBase}.add`)}
      </button>
    </div>
  );
}
